import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter, map, startWith } from 'rxjs';

import { TranslocoDirective } from '@jsverse/transloco';

import { NavbarItem } from './models/navbar.model';
import { AuthService } from '../../../core/auth/auth.service';
import { Role } from '../../../core/auth/models/role';
import { ContextService } from '../../../core/context.service';
import { PortalService, ADMIN_PATH } from '../../../core/portal.service';
import { Icon } from '../icon/icon';
import { IconName } from '../icon/icon-name';
import { TenantSwitcher } from '../tenant-switcher/tenant-switcher';
import { UserCard } from '../user-card/user-card';

/**
 * Outline → filled ("-f1"/"-f") menu-glyph pairs from the icon sprite. The
 * selected nav item swaps to the filled variant (DS LeftMenuNavItem spec);
 * icons without a filled twin (e.g. icon-cloud) keep their outline glyph.
 */
const FILLED_ICON: Partial<Record<IconName, IconName>> = {
  'icon-menu-alerts1': 'icon-menu-alerts-f1',
  'icon-menu-appswidgets1': 'icon-menu-appswidgets-f1',
  'icon-menu-content1': 'icon-menu-content-f1',
  'icon-menu-contentproviders1': 'icon-menu-contentproviders-f1',
  'icon-menu-device-configurations': 'icon-menu-device-configurations-f',
  'icon-menu-displays1': 'icon-menu-displays-f1',
  'icon-menu-inspector1': 'icon-menu-inspector-f1',
  'icon-menu-layouts1': 'icon-menu-layouts-f1',
  'icon-menu-medialibrary1': 'icon-menu-medialibrary-f1',
  'icon-menu-monitor1': 'icon-menu-monitor-f1',
  'icon-menu-operations': 'icon-menu-operations-f',
  'icon-menu-settings1': 'icon-menu-settings-f1',
  'icon-menu-tenants': 'icon-menu-tenants-f',
  'icon-menu-updates': 'icon-menu-updates-f',
  'icon-menu-users': 'icon-menu-users-f',
};

/**
 * The primary sidebar (design system "LeftMenu"): the portal identity at the
 * top (tenant switcher, or the amber admin capsule in the back-office), the
 * role-filtered navigation list, and the signed-in user's card pinned to the
 * bottom.
 *
 * This is the reference implementation of the modern component conventions —
 * signal input()/output(), inject(), OnPush, and toSignal() at the RxJS
 * boundary (no manual subscribe, no async pipe). Match it when rebuilding
 * other components. See docs/architecture-foundations.md.
 */
@Component({
  selector: 'app-navbar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, Icon, TenantSwitcher, TranslocoDirective, UserCard],
  templateUrl: './navbar.component.html',
})
export class Navbar {
  private readonly contextService = inject(ContextService);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly portalService = inject(PortalService);

  readonly navbarItems = input<NavbarItem[]>([]);

  protected readonly inAdminPortal = this.portalService.inAdminPortal;

  protected readonly organizations = toSignal(this.contextService.organizations$, {
    initialValue: [],
  });

  private readonly roles = toSignal(this.contextService.roles$, {
    initialValue: [] as string[],
  });

  protected readonly isSuperAdmin = computed(() =>
    this.roles().includes(Role.SUPER_ADMIN),
  );

  protected readonly currentOrganization = toSignal(
    this.contextService.organization$,
    { initialValue: null },
  );

  protected onSwitchOrganization(organizationId: string): void {
    this.authService.switchOrganization(organizationId);
  }

  protected onGoAdmin(): void {
    this.router.navigate([ADMIN_PATH]);
  }

  // The active-route highlight recomputes on every navigation. startWith fires
  // it once for the initial URL (NavigationEnd only emits on later navigations).
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map((e) => (e as NavigationEnd).urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  protected readonly visibleItems = computed(() => {
    const roles = this.roles();
    return this.navbarItems().filter(
      (item) => !item.roles?.length || item.roles.some((r) => roles.includes(r)),
    );
  });

  private readonly organizationName = computed(
    () => this.currentOrganization()?.name,
  );

  protected buildRoute(item: NavbarItem): string[] {
    // Admin items are root-absolute and never carry the org prefix. Split into
    // segments — routerLink URL-encodes a '/' inside a single array element, so
    // ['/', 'admin/tenants'] would produce the broken '/admin%2Ftenants'.
    if (item.absolute) {
      return ['/', ...item.route.split('/')];
    }
    const org = this.organizationName();
    return org ? ['/', org, item.route] : ['/', item.route];
  }

  /** Filled glyph for the selected state: explicit override → sprite pair → outline. */
  protected activeIcon(item: NavbarItem): IconName {
    if (item.iconActive) return item.iconActive;
    // Only called from behind the template's @if (item.icon) guard; the
    // placeholder fallback just satisfies strict typing.
    const outline = item.icon ?? 'icon-placeholder';
    return FILLED_ICON[outline] ?? outline;
  }

  protected isActive(item: NavbarItem): boolean {
    const org = this.organizationName();
    const path = item.absolute || !org ? `/${item.route}` : `/${org}/${item.route}`;
    const target = path.replace(/\/+/g, '/');
    const url = this.currentUrl().split('?')[0];
    return url === target || url.startsWith(target + '/');
  }
}
